import { Component, inject, OnInit } from '@angular/core';
import { IPendingRequest } from '../models/pending-request.model';
import { MatTableModule } from '@angular/material/table';
import { PendingRequestsService } from '../services/pending-requests.service';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SelectionModel } from '@angular/cdk/collections';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SendToBankComponent } from '../send-to-bank/send-to-bank.component';
import { PaymentFileComponent } from '../payment-file/payment-file.component';
import { RequestDetailsViewComponent } from '../request-details-view/request-details-view.component';
import { Router } from '@angular/router';
import { LoggedInUserService } from '../../services/common/logged-in-user.service';

@Component({
  selector: 'app-requests-pending-my-action',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatProgressSpinnerModule, MatCheckboxModule, MatDialogModule],
  templateUrl: './requests-pending-my-action.component.html',
  styleUrl: './requests-pending-my-action.component.scss',
  providers: [DatePipe, TitleCasePipe]
})
export class RequestsPendingMyActionComponent implements OnInit {
  pendingDataSource: (IPendingRequestsPosition | IGroup)[];
  reviewedDataSource: (IPendingRequestsPosition | IGroup)[];
  pendingRequests: IPendingRequest[];
  reviewedRequests: IPendingRequest[];
  pendingRequestsService: PendingRequestsService = inject(PendingRequestsService);
  pendingIsLoading: boolean = false;
  reviewedIsLoading: boolean = false;
  operationsDisabled: boolean = true;
  sendToBankProcessingEnabled: boolean = false;
  producePaymentFileProcessingEnabled: boolean = false;
  selection = new SelectionModel<IPendingRequestsPosition | IGroup>(true, []);
  pendingDisplayedColumns: string[] = ['number', 'request', 'description', 'entryTime', 'amount', 'enteredBy'];
  reviewedDisplayedColumns: string[] = ['select', 'number', 'request', 'description', 'entryTime', 'amount', 'enteredBy'];

  constructor(
    private modal: MatDialog,
    private router: Router,
    private userService: LoggedInUserService
  ) { }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.reviewedDataSource.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
        this.selection.clear() :
        this.reviewedDataSource.forEach(row => this.selection.select(row));
  }

  requestSelected(): void {
    this.operationsDisabled = this.selection.selected.length === 0;
  }

  sendToBank(): void {
    const requests = this.selection.selected.map(item => {
      item = item as IPendingRequestsPosition;
      return {requestNumber: item.request.requestNumber, title: item.request.title};
    });

    this.modal.open(SendToBankComponent, {
      width: '1000px',
      disableClose: true,
      data: { requests: requests }
    });

    this.modal.afterAllClosed.subscribe(() => {
      this.loadData();
    });
  }

  producePaymentFile(): void {
    const requests = this.selection.selected
    .filter(item => {
      item = item as IPendingRequestsPosition;
      return item.request?.requestNumber !== null && item.request?.requestNumber !== undefined;
    }).map(item => {
      item = item as IPendingRequestsPosition;
      return {requestNumber: item.request.requestNumber, title: item.request.title};
    });

    this.modal.open(PaymentFileComponent, {
      width: '1600px',
      disableClose: true,
      data: { requests: requests }
    });

    this.modal.afterAllClosed.subscribe(() => {
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.userService.currentUser$.subscribe(data => {
      if(data == null){
        this.router.navigate(["/unauthorized"]);
      }else{
        this.loadData();
      }
    });
  }

  loadData(): void {
    this.pendingIsLoading = true;
    this.reviewedIsLoading = true;

    this.pendingRequestsService.getPendingRequests().subscribe({
      next: (pendingRequests) => {
        this.pendingIsLoading = false;
        this.pendingRequests = pendingRequests;
        this.pendingDataSource = this.groupRequests(pendingRequests);
      },
      error: (error) => {
        this.pendingIsLoading = false;
      }
    });

    this.pendingRequestsService.getReviewedRequests().subscribe({
      next: (reviewedRequests) => {
        this.reviewedIsLoading = false;
        this.reviewedRequests = reviewedRequests;
        this.reviewedDataSource = this.groupRequests(reviewedRequests);
        if (reviewedRequests.some(request => request.currency === "NGN" && request.howPaid === "C")) {
          this.sendToBankProcessingEnabled = true;
        }
      },
      error: (error) => {
        this.reviewedIsLoading = false;
      }
    });
  }

  groupRequests(requests: IPendingRequest[]): (IPendingRequestsPosition | IGroup)[] {
    const groupedRequests: (IPendingRequestsPosition | IGroup)[] = [];
    const groups: { [key: string]: IPendingRequestsPosition[] } = {};
  
    requests.forEach(request => {
      const group = request.statusDescription;
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push({request: request, position: groups[group].length + 1});
    });
  
    for (const group in groups) {
      groupedRequests.push({ group: group});
      groupedRequests.push(...groups[group]);
    }
  
    return groupedRequests;
  }

  isGroup(index, item): boolean{
    return item.group;
  }

  openRequestDetails(requestNumber: number): void {
    this.modal.open(RequestDetailsViewComponent, {
      width: '1000px',
      data: { requestNumber: requestNumber }
    });
  }

  openEnterRequest(requestNumber: number): void {
    this.router.navigate(['/enter-request', requestNumber]);
  }
}

export interface IGroup {
  group: string;
}

export interface IPendingRequestsPosition {
  request: IPendingRequest;
  position: number;
}